/**
 * Whether a tenant holds a Module Entitlements row.
 *
 * The page lists two products by legacy spellings (`crm`, `chatbot_manager`)
 * while a commercially created tenant holds the canonical codes
 * (`sales_console`, `chatbot`). The server carries `canonical_code` on every
 * row; comparing only `api_name` showed Assistants OFF for a tenant that had
 * it. Either spelling counts, so tenants still carrying the literal legacy
 * code keep reading ON too.
 */
export function moduleIsHeld(module, enabledModules) {
  const held = enabledModules || [];
  return held.includes(module.canonical_code || module.api_name)
    || held.includes(module.api_name);
}

/** The rows the page offers as toggleable products, in the server's order. */
export function sellableProducts(availableModules) {
  return (availableModules || [])
    .filter((m) => m.product_category === 'sellable' && !m.duplicate_of)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/**
 * How a row that ANOTHER PRODUCT INCLUDES is shown: the includer's name when
 * it must render as "Included with …" (no switch of its own), or null when it
 * keeps a switch.
 *
 * `included_with` is the server's (`get_available_modules`): Live Chat comes
 * with Assistants; Form Builder and Survey Builder are the legacy codes Engage
 * replaced and grants internally (owner, 27 Sep 2026: one Engage
 * application). None of them is offered as a product to switch ON.
 *
 * The one exception keeps a legacy holder removable: a tenant that holds the
 * row WITHOUT the product that includes it (a pre-Engage Form Builder tenant)
 * gets its switch, so the row can be turned off — and once it is off it is a
 * label again, so it can never be switched back on from here.
 *
 * `holds(row)` answers whether the tenant holds a row; each page brings its
 * own, because they read held state from different places.
 */
export function includedWithName(module, availableModules, holds) {
  if (!module?.included_with) return null;
  const includer = (availableModules || []).find((m) => m.api_name === module.included_with);
  if (holds(module) && !(includer && holds(includer))) return null;
  return includer?.name || 'another product';
}

/**
 * The rows that may be a tenant's LANDING PAGE: every sellable product, plus
 * every row another product INCLUDES. A landing page is a place, not a
 * product — Form Builder stopped being offered as a product when it was
 * folded into Engage (27 Sep 2026), but a tenant that lands on /form-builder
 * still lands there, and the Module Entitlements dialog auto-corrects (and
 * SAVES) a landing page that is not among its options. Other legacy rows
 * (Field Service) stay out, exactly as before.
 */
export function landingCandidates(availableModules) {
  const sellable = sellableProducts(availableModules);
  return [...sellable, ...(availableModules || []).filter(
    (m) => m.included_with && !m.duplicate_of && !sellable.includes(m))];
}

/**
 * Is this row a product RETIRED INTO another one? The server's shape for it
 * (`get_available_modules`, `SUPERSEDED_BY`): `product_category: 'legacy'`
 * with `included_with` naming the replacement — Form Builder and Survey
 * Builder, replaced by Engage (owner, 27 Sep 2026). Live Chat is included
 * with Assistants but was never retired, so it is not one of these.
 */
export function isRetiredInto(module) {
  return module?.product_category === 'legacy' && !!module?.included_with;
}

/**
 * Whether a row gets a TILE of its own on the Modules tab and the Products
 * page's Legacy section.
 *
 * Closure audit 10-06, M6: an Engage tenant was shown "Form Builder — Dynamic
 * form creation" and "Survey Builder" beside Engage. "Included with Engage"
 * under them did not stop them reading as two more applications, which is
 * exactly what the owner's "ONE Engage application" rules out. So a row
 * retired into another product is shown ONLY to a tenant that holds it
 * without holding its replacement — a pre-Engage holder, who keeps a switch
 * to turn it off (`includedWithName` returns null for that case). Everyone
 * else, Engage tenant or not, sees Engage and nothing of what it replaced.
 *
 * A legacy alias (`crm`, `duplicate_of`) never gets a tile; every other row
 * is unchanged.
 */
export function showsAsOwnTile(module, availableModules, holds) {
  if (!module || module.duplicate_of) return false;
  if (!isRetiredInto(module)) return true;
  const includer = (availableModules || []).find((m) => m.api_name === module.included_with);
  return holds(module) && !(includer && holds(includer));
}

/**
 * Customer-product adoption for a tenant-list card.
 *
 * A tenant document stores runtime entitlement codes, including bundled and
 * compatibility plumbing.  The Products page used to print that raw array;
 * after the list endpoint stopped returning it, every tenant instead printed
 * "No modules enabled".  Neither representation is a product view.  Resolve
 * the stored codes through the same catalogue and retirement rules as the
 * edit dialog, while retaining the raw count as an explicitly named runtime
 * fact.
 */
export function tenantProductAdoption(enabledModules, availableModules) {
  const codes = Array.isArray(enabledModules) ? enabledModules : [];
  const holds = (m) => moduleIsHeld(m, codes);
  const seen = new Set();
  const products = (availableModules || [])
    .filter((m) => ['sellable', 'legacy'].includes(m.product_category))
    .filter((m) => holds(m) && showsAsOwnTile(m, availableModules, holds))
    .filter((m) => {
      const code = m.canonical_code || m.api_name;
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    })
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  return { products, runtimeEntitlementCount: codes.length };
}

/**
 * Product -> the CRM route a tenant can LAND on (the Products page's Default
 * Landing Page). Moved here from ModulesPage.jsx so it can be tested.
 *
 * Verified against Amroar_CRM/frontend/src/pages/Setup/SetupPage.jsx (the
 * ModuleMenuButton `path` for each moduleCode) and platform/utils/
 * appRouteMap.js. Booking's real route is /booking/scheduler/overview.
 * "reporting" -> "/reports" is the best match found (lower confidence).
 *
 * `engage` -> `/engage`, closure audit 10-06 (M6): it had no entry, so an
 * Engage tenant could not choose Engage. The route is the platform's
 * `app_registry` `default_route` for Engage (cluvik-platform
 * `modules/engage/authz/catalog.py`, `engage_app_registry_row`), which the
 * server's landing-page check accepts because it reads every registry
 * `default_route` (`_build_dynamic_landing_pages`). `/form-builder` is that
 * row's `legacy_route`, so a user landing there was already sent to Engage.
 */
export const PRODUCT_LANDING_ROUTES = Object.freeze({
  sales_console: '/crm-platform',
  task_manager: '/task-manager',
  form_builder: '/form-builder',
  flow_builder: '/flows',
  survey_builder: '/survey-builder-v2',
  engage: '/engage',
  // `/booking`, the Platform registry's `default_route` (its index redirects
  // to scheduler/overview). This said `/booking/scheduler/overview`, which the
  // server's landing allow-list (`_build_dynamic_landing_pages`: static list +
  // every registry default/legacy route) refuses with 400 — so choosing
  // Booking could never be saved, and a tenant already on `/booking` was shown
  // as holding an invalid landing page (NX-S01-02). Pinned against the
  // Platform manifest by backend/tests/test_s01_platform_manifest_parity.py.
  booking: '/booking',
  chatbot_manager: '/setup/chatbot-manager',
  docflow: '/setup/docflow',
  reporting: '/reports',
});

/**
 * The Default Landing Page options for a tenant: the landing candidates it
 * holds, with a known route — minus a retired app on a tenant that holds its
 * replacement (`showsAsOwnTile`). An Engage tenant is offered Engage, never
 * Form Builder or Survey Builder; a pre-Engage Form Builder holder still
 * lands on /form-builder.
 */
export function landingOptions(availableModules, holds, routes = PRODUCT_LANDING_ROUTES) {
  return landingCandidates(availableModules)
    .filter(holds)
    .filter((m) => showsAsOwnTile(m, availableModules, holds))
    .filter((m) => routes[m.api_name])
    .map((m) => ({ path: routes[m.api_name], label: m.name }));
}

/**
 * Where a saved landing page goes when it is no longer an option.
 *
 * Kept when still offered. A retired app's page moves to its REPLACEMENT's
 * page when that is offered — an Engage tenant saved on /form-builder lands
 * on /engage, the same place the platform already sent it — rather than to
 * whatever happens to be first. Otherwise the first option, or '' for none.
 */
export function correctedLanding(saved, options, availableModules, holds,
                                 routes = PRODUCT_LANDING_ROUTES) {
  const offered = (options || []).map((o) => o.path);
  if (offered.includes(saved)) return saved;
  const retired = (availableModules || []).find(
    (m) => isRetiredInto(m) && routes[m.api_name] === saved);
  const replacement = retired && routes[retired.included_with];
  if (replacement && offered.includes(replacement)) return replacement;
  return offered[0] || '';
}
